import rawTable from "@cogcoin/genesis/scoring_bundle/coglex_token_table.json" with {
  type: "json",
};

/**
 * The Coglex token vocabulary as shipped by `@cogcoin/genesis`. This is
 * the protocol's actual sentence vocabulary — a superset of BIP-39 that
 * adds 1668 "base" words, 229 irregulars, 60 function words, 41
 * suffixes, 33 punctuation marks, 12 prefixes, and 5 control tokens, on
 * top of the 2048 BIP-39 entries. 4096 tokens total.
 *
 * The BIP-39 wordlist is reserved for the per-puzzle blend; the
 * sentence the player composes is validated against this richer vocab.
 */

export type CoglexCategory = "b" | "s" | "c" | "p" | "x" | "i" | "f" | "w";

export interface CoglexToken {
  readonly word: string;
  readonly category: CoglexCategory;
  readonly id: number;
}

interface RawTable {
  readonly tokens: readonly (readonly string[])[];
  readonly total: number;
}

const table = rawTable as unknown as RawTable;

export const COGLEX_TOKEN_COUNT = table.total;

if (table.tokens.length !== COGLEX_TOKEN_COUNT) {
  throw new Error(
    `coglex token table is malformed: declared ${COGLEX_TOKEN_COUNT.toString()} but found ${table.tokens.length.toString()}`,
  );
}

export const COGLEX_TOKENS: readonly CoglexToken[] = Object.freeze(
  table.tokens.map((entry, id) => {
    const word = entry[0];
    const category = entry[1];
    if (word === undefined || category === undefined) {
      throw new Error(`coglex token table entry ${id.toString()} is malformed`);
    }
    return { word, category: category as CoglexCategory, id };
  }),
);

// Categories that represent a standalone, type-able word. Suffixes,
// prefixes, control tokens, and punctuation chars don't get suggested
// or accepted as a bare word — the encoder composes them into longer
// tokens, but a player wouldn't type them on their own.
const FREESTANDING_CATEGORIES = new Set<CoglexCategory>(["b", "w", "i", "f"]);

const FREESTANDING_WORDS_SORTED: readonly string[] = Object.freeze(
  COGLEX_TOKENS.filter((t) => FREESTANDING_CATEGORIES.has(t.category))
    .map((t) => t.word)
    .sort(),
);

const FREESTANDING_WORD_SET: ReadonlySet<string> = new Set(FREESTANDING_WORDS_SORTED);

/// Returns `true` if the word is a freestanding Coglex token — meaning
/// the player could legitimately type it as a standalone word.
export function isCoglexToken(word: string): boolean {
  return FREESTANDING_WORD_SET.has(word);
}

/**
 * Returns up to `max` Coglex tokens that start with `prefix`. Only
 * freestanding-word categories are searched — no suffixes/prefixes/
 * punctuation, since the player wouldn't pick those from an
 * autocomplete dropdown.
 */
export function coglexPrefixMatches(prefix: string, max: number): string[] {
  if (prefix.length === 0 || max <= 0) {
    return [];
  }
  let lo = 0;
  let hi = FREESTANDING_WORDS_SORTED.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (FREESTANDING_WORDS_SORTED[mid]! < prefix) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }
  const result: string[] = [];
  for (let i = lo; i < FREESTANDING_WORDS_SORTED.length && result.length < max; i++) {
    const word = FREESTANDING_WORDS_SORTED[i]!;
    if (!word.startsWith(prefix)) break;
    result.push(word);
  }
  return result;
}
